import { Responsive, WidthProvider } from 'react-grid-layout';

import { ReportFilterMultiSelect } from '../../../../../../components/report-filter-buttons/report-filter-multi-select';
import { KEYS } from '../../../../../../providers/layout-hook';
import usePersistentState from '../../../../../../providers/use-persistent-state';

import './styles.scss';

import DecayWidget from '../../../../../../components/v2/widgets/decay/decay-widget';
import RiskMetricWidget from '../../../../../../components/v2/widgets/risk-metric/risk-metric-widget';
import { WidgetKeys } from '../../../../../../providers/model/custom-widget';
import { useContext } from 'react';
import { PortfolioManagementContext } from '../../../../../../providers/portfolio-management';

type RiskWidget = {
  key: string;
  title: string;
  dataType: string;
};

const ResponsiveGridLayout = WidthProvider(Responsive);

interface Iprops {
  riskMetricList: any[];
  currency: string;
  minIndex: number;
  maxIndex: number;
  isLoading: boolean;
}

const RiskGraphs = ({ riskMetricList, currency, minIndex, maxIndex, isLoading }: Iprops) => {
  const PATH = ['risk-graphs'];
  const riskMetricListArray = [
    ...riskMetricList,
    {
      label: 'PNL',
      value: 'PNL',
      isVisible: true,
    },
    { value: 'deltaDecay', label: 'Delta Decay', isVisible: true },
    { value: 'thetaDecay', label: 'Theta Decay', isVisible: true },
  ];

  const DEFAULT_WIDGETS: RiskWidget[] = [
    {
      key: 'risk-graph-pnl',
      dataType: 'PNL',
      title: `PNL`,
    },
  ];

  const DEFAULT_LAYOUTS = {
    [KEYS.LG]: [{ i: `risk-graph-pnl`, x: 0, y: 0, w: 12, h: 4 }],
    [KEYS.MD]: [{ i: `risk-graph-pnl`, x: 0, y: 0, w: 12, h: 4 }],
    [KEYS.SM]: [{ i: `risk-graph-pnl`, x: 0, y: 0, w: 12, h: 4 }],
    [KEYS.XS]: [{ i: `risk-graph-pnl`, x: 0, y: 0, w: 12, h: 4 }],
    [KEYS.XXS]: [{ i: `risk-graph-pnl`, x: 0, y: 0, w: 12, h: 4 }],
  };

  const { selectedAccountOption: accountOption } = useContext(PortfolioManagementContext);
  const [widgets, setWidgets] = usePersistentState<RiskWidget[]>(
    PATH,
    'widgets_risk',
    DEFAULT_WIDGETS,
  );
  const [layouts, setLayouts] = usePersistentState<any>(PATH, 'layout_risk', DEFAULT_LAYOUTS);

  const handleLayoutChange = (
    layout: ReactGridLayout.Layout[],
    layouts: ReactGridLayout.Layouts,
  ) => {
    setLayouts(layouts);
  };

  const setFiltersHandler = (values: string[]) => {
    const updatedWidgets = values.map((value) => {
      const label = riskMetricListArray.find((filter) => filter.value === value)?.label || value;
      return {
        key: `risk-graph-${value}`,
        title: `${label}`,
        dataType: value,
      };
    });

    if (values.length === 1) {
      setWidgets(updatedWidgets);
      setLayouts(() => ({
        [KEYS.LG]: [{ i: `risk-graph-${values[0]}`, x: 0, y: 0, w: 12, h: 4 }],
        [KEYS.MD]: [{ i: `risk-graph-${values[0]}`, x: 0, y: 0, w: 12, h: 4 }],
        [KEYS.SM]: [{ i: `risk-graph-${values[0]}`, x: 0, y: 0, w: 12, h: 4 }],
        [KEYS.XS]: [{ i: `risk-graph-${values[0]}`, x: 0, y: 0, w: 12, h: 4 }],
        [KEYS.XXS]: [{ i: `risk-graph-${values[0]}`, x: 0, y: 0, w: 12, h: 4 }],
      }));
      return;
    }

    const updatedLayouts = values.map((value, index) => ({
      i: `risk-graph-${value}`,
      x: index % 2 === 0 ? 0 : 6,
      y: Math.floor(index / 2),
      w: 6,
      h: 4,
    }));

    setWidgets(updatedWidgets);
    setLayouts(() => ({
      [KEYS.LG]: updatedLayouts,
      [KEYS.MD]: updatedLayouts,
      [KEYS.SM]: values.map((value, index) => ({
        i: `risk-graph-${value}`,
        x: 0,
        y: index,
        w: 12,
        h: 4,
      })),
      [KEYS.XS]: values.map((value, index) => ({
        i: `risk-graph-${value}`,
        x: 0,
        y: index,
        w: 12,
        h: 4,
      })),
      [KEYS.XXS]: values.map((value, index) => ({
        i: `risk-graph-${value}`,
        x: 0,
        y: index,
        w: 12,
        h: 4,
      })),
    }));
  };

  if (isLoading) {
    return null;
  }

  return (
    <div className="risk-graphs">
      <div className="risk-graph-options">
        <ReportFilterMultiSelect
          titleFilter=""
          dataArr={riskMetricListArray}
          selected={widgets.map((widget) => widget.dataType)}
          setSelected={setFiltersHandler}
          disableBorder
        />
      </div>

      <ResponsiveGridLayout
        className="grid"
        layouts={layouts}
        autoSize={true}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 12, sm: 12, xs: 1, xxs: 1 }}
        resizeHandles={['se']}
        draggableHandle=".drag-area"
        onLayoutChange={handleLayoutChange}
        useCSSTransforms={true}
      >
        {widgets.map((widget) => {
          const widgetTitle = `${accountOption.name}: ${widget.title} - ${currency}`;

          return (
            <div key={widget.key} className={'risk-graph-widget-container'}>
              {widget.dataType === 'deltaDecay' || widget.dataType === 'thetaDecay' ? (
                <DecayWidget
                  widget={{
                    key: widget.key as WidgetKeys,
                    title: widgetTitle,
                  }}
                  dataType={widget.dataType}
                  currency={currency}
                />
              ) : (
                <RiskMetricWidget
                  widget={{
                    key: 'risk-metric' as WidgetKeys,
                    title: widgetTitle,
                  }}
                  id={widget.key}
                  minIndex={minIndex}
                  maxIndex={maxIndex}
                />
              )}
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

export default RiskGraphs;
